/**
 * Confirmation dialog (delete group, archive profile — PRD asks for explicit confirmation on
 * destructive actions). Built on `Modal`; the caller owns the mutation.
 */

import type { ReactNode } from 'react';
import { Modal } from './Modal';
import { Spinner } from './Spinner';

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints the confirm button as destructive. */
  destructive?: boolean;
  /** Mutation in flight: buttons disable and the confirm label shows a spinner. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      title={title}
      size="sm"
      closeOnBackdrop={!busy}
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <button type="button" className="table-action" onClick={onCancel} disabled={busy}>
            <span className="table-action__label">{cancelLabel}</span>
          </button>
          <button
            type="button"
            className={destructive ? 'table-action btn-danger' : 'btn-primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <Spinner size="sm" /> : null}
            <span className={destructive ? 'table-action__label' : undefined}>{confirmLabel}</span>
          </button>
        </>
      }
    >
      {message}
    </Modal>
  );
}

export default ConfirmDialog;
